import React, { createContext, useContext, type ReactNode } from 'react';
import { ConfigProvider, theme } from 'antd';
import { useTheme, type EffectiveTheme, type ThemeMode } from '../hooks/useTheme';
import { useIsMobile } from '../hooks/useIsMobile';

interface ThemeContextValue {
  mode: ThemeMode;
  effective: EffectiveTheme;
  setMode: (next: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used within ThemeProvider');
  return ctx;
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const themeState = useTheme();
  const isMobile = useIsMobile();

  return (
    <ThemeContext.Provider value={themeState}>
      <ConfigProvider
        theme={{
          algorithm: themeState.effective === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
          hashed: false,
          // Desktop control heights (32/24px) are below a comfortable touch target.
          ...(isMobile ? { token: { controlHeight: 40, controlHeightSM: 32 } } : {}),
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}
